import React from 'react';
import { WorkflowNode } from './components/node';
import type { WorkflowNodeViewData } from './components/node';
import { WORKFLOW_FLOW_NODE_WIDTH } from './workflow-flow-layout.constants';
import type { FanOutGroupSummary } from './workflow-run-fan-out-group';

/**
 * Renders a collapsed for-each fan-out as a single stacked "deck" card: the
 * underlying node card (so the icon, title and accent match the rest of the
 * graph), offset shadow layers behind it to signal multiplicity, and a `×N`
 * count badge on the bottom-right corner.
 *
 * The card is deliberately kept clean — no status badge or failure chip. Status
 * lives in the step inspector's instances list (a per-status breakdown plus a
 * status badge on every iteration), which is one click away and the right place
 * for per-iteration detail. Clicking the card opens it.
 *
 * This is the collapse + drill-in pattern that Step Functions, Airflow and
 * Camunda use for fan-out: the graph stays O(1) wide no matter how many
 * iterations ran.
 */
export const WorkflowRunFanOutCard: React.FC<{
  node: WorkflowNodeViewData;
  fanOut: FanOutGroupSummary;
  className?: string;
}> = ({ node, fanOut, className }) => {
  return (
    <div
      className="relative"
      style={{ width: WORKFLOW_FLOW_NODE_WIDTH }}
      data-testid="workflow-run-fan-out-card"
    >
      {/* Two offset layers peeking out behind the real card read as a short
          stack without implying an exact depth. Non-interactive. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 translate-x-2 translate-y-2 rounded-2xl border border-border/60 bg-card/60 shadow-xs"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 translate-x-1 translate-y-1 rounded-2xl border border-border/70 bg-card/80 shadow-xs"
      />
      <div className="relative">
        <WorkflowNode node={node} className={className} status={null} />
        {/* Count badge: how many iterations this one card stands in for. Pill on
            the bottom-right corner, clear of the deck layers that peek out
            below/right. Centred glyph. */}
        <div
          title={`${fanOut.count} iterations`}
          className="absolute -bottom-2.5 -right-2.5 grid h-7 min-w-7 place-items-center rounded-full border border-primary/20 bg-primary px-2 text-xs font-semibold text-primary-foreground shadow-sm tabular-nums"
        >
          {/* The glyph is nudged down ~1px: a fixed-height pill reserves the
              font's descender space at the bottom, so centering the line box
              leaves ascender-only text (digits, ×) visually high. */}
          <span className="leading-none translate-y-[0.5px]">
            ×{fanOut.count}
          </span>
        </div>
      </div>
    </div>
  );
};
